import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type Phase = 'loading' | 'invalid' | 'pin' | 'scanning' | 'result';

interface KioskInfo {
  id: string;
  edition_id: string;
  label: string | null;
  edition_label: string;
  course_name: string;
  scheduled_at: string;
}

interface ScanResult {
  ok: boolean;
  action?: 'check_in' | 'check_out';
  participant?: { nome: string; cognome: string; azienda?: string };
  error?: string;
}

// ─── QR Scanner (reused from admin, but standalone) ───────────────────────────
function KioskScanner({ kioskToken, onScan }: { kioskToken: string; onScan: (result: ScanResult) => void }) {
  const divRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<any>(null);
  const scanningRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    async function start() {
      if (!divRef.current) return;
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scannerRef.current = new Html5Qrcode('kiosk-qr-reader');
        await scannerRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 280, height: 280 } },
          async (decodedText: string) => {
            if (scanningRef.current || !mounted) return;
            scanningRef.current = true;

            let enrollmentToken = decodedText.trim();
            try {
              const url = new URL(decodedText);
              const t = url.searchParams.get('token');
              if (t) enrollmentToken = t;
            } catch {}

            try {
              const res = await fetch('/api/kiosk/scan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kioskToken, enrollmentToken }),
              });
              const data = await res.json();
              if (res.ok) {
                onScan({ ok: true, action: data.action, participant: data.participant });
              } else {
                onScan({ ok: false, error: data.error ?? 'Errore' });
              }
            } catch {
              onScan({ ok: false, error: 'Errore di rete' });
            }

            // Reset after 3s
            setTimeout(() => { scanningRef.current = false; }, 3000);
          },
          () => {}
        );
      } catch (e: any) {
        onScan({ ok: false, error: 'Impossibile avviare la fotocamera: ' + (e?.message ?? e) });
      }
    }

    start();
    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [kioskToken]);

  return (
    <div id="kiosk-qr-reader" ref={divRef} style={{ width: '100%', maxWidth: 480, borderRadius: 12, overflow: 'hidden' }} />
  );
}

// ─── Main Kiosk Page ──────────────────────────────────────────────────────────
export default function KioskPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';

  const [phase, setPhase] = useState<Phase>('loading');
  const [kiosk, setKiosk] = useState<KioskInfo | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);

  // Validate token on load
  useEffect(() => {
    if (!token) { setPhase('invalid'); return; }
    fetch(`/api/kiosk/info?t=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (data.kiosk) { setKiosk(data.kiosk); setPhase('pin'); }
        else setPhase('invalid');
      })
      .catch(() => setPhase('invalid'));
  }, [token]);

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    try {
      const res = await fetch('/api/kiosk/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, pin }),
      });
      if (res.ok) {
        setPhase('scanning');
      } else {
        const data = await res.json();
        setPinError(data.error ?? 'PIN non corretto');
        setPin('');
      }
    } catch {
      setPinError('Errore di rete. Riprova.');
    }
  };

  const handleScanResult = (result: ScanResult) => {
    setScanResult(result);
    setTimeout(() => setScanResult(null), 3500);
  };

  // ── Screens ────────────────────────────────────────────────────────────────

  const baseStyle: React.CSSProperties = {
    minHeight: '100vh', background: '#0a1628',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontFamily: 'system-ui, sans-serif',
    padding: '2rem',
  };

  if (phase === 'loading') return (
    <div style={baseStyle}>
      <div style={{ width: 48, height: 48, border: '4px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (phase === 'invalid') return (
    <div style={baseStyle}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
      <h2 style={{ margin: '0 0 0.5rem' }}>Token non valido o scaduto</h2>
      <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
        Questo terminale non è autorizzato. Richiedere un nuovo token all'amministratore.
      </p>
    </div>
  );

  if (phase === 'pin') return (
    <div style={baseStyle}>
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🔐</div>
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.75rem' }}>{kiosk?.course_name}</h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', margin: 0 }}>{kiosk?.edition_label}</p>
      </div>
      <form onSubmit={handlePinSubmit} style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Inserisci PIN"
          value={pin}
          onChange={e => setPin(e.target.value)}
          autoFocus
          style={{
            padding: '1rem', fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.3em',
            border: pinError ? '2px solid #f87171' : '2px solid rgba(255,255,255,0.2)',
            borderRadius: 12, background: 'rgba(255,255,255,0.08)', color: '#fff',
            outline: 'none',
          }}
        />
        {pinError && <p style={{ color: '#f87171', textAlign: 'center', margin: 0 }}>{pinError}</p>}
        <button type="submit" style={{
          padding: '1rem', fontSize: '1rem', fontWeight: 700, borderRadius: 12,
          background: '#3b82f6', border: 'none', color: '#fff', cursor: 'pointer',
        }}>
          Accedi
        </button>
      </form>
    </div>
  );

  // Scanning phase
  return (
    <div style={{ ...baseStyle, justifyContent: 'flex-start', paddingTop: '2rem' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem', width: '100%' }}>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8125rem', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Terminale di accesso
        </p>
        <h1 style={{ margin: '0 0 0.2rem', fontSize: '1.5rem' }}>{kiosk?.course_name}</h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', margin: 0, fontSize: '0.9rem' }}>{kiosk?.edition_label}</p>
      </div>

      {/* Scanner */}
      <KioskScanner kioskToken={token} onScan={handleScanResult} />

      {/* Result toast */}
      {scanResult && (
        <div style={{
          marginTop: '2rem', padding: '1.5rem 2rem', borderRadius: 16, textAlign: 'center',
          background: scanResult.ok
            ? (scanResult.action === 'check_in' ? '#166534' : '#92400e')
            : '#7f1d1d',
          width: '100%', maxWidth: 480,
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
            {scanResult.ok ? (scanResult.action === 'check_in' ? '✅' : '👋') : '❌'}
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
            {scanResult.ok
              ? (scanResult.action === 'check_in' ? 'Entrata registrata' : 'Uscita registrata')
              : scanResult.error}
          </div>
          {scanResult.participant && (
            <div style={{ marginTop: '0.5rem', fontSize: '1rem', color: 'rgba(255,255,255,0.85)' }}>
              {scanResult.participant.nome} {scanResult.participant.cognome}
              {scanResult.participant.azienda && ` — ${scanResult.participant.azienda}`}
            </div>
          )}
        </div>
      )}

      {!scanResult && (
        <p style={{ marginTop: '1.5rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>
          Inquadra il QR code dal badge
        </p>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}
